import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Box, Button, Paper, Typography } from '@mui/material';
import React from 'react';

import {
    createExerciseVideoThumbnail,
    getCachedExerciseVideoBlob,
    getCachedExerciseVideoThumbnail,
    setCachedExerciseVideoThumbnail,
} from './exerciseVideoCache';

interface Props {
    title: string;
    videoUrl?: string;
}

interface State {
    cachedVideoUrl?: string;
    hasLoadError: boolean;
    isLoading: boolean;
    thumbnailUrl?: string;
}

class ExerciseVideoPreview extends React.Component<Props, State> {
    private aborted = false;
    playbackVideoRef = React.createRef<HTMLVideoElement>();

    state: State = {
        cachedVideoUrl: undefined,
        hasLoadError: false,
        isLoading: false,
        thumbnailUrl: this.props.videoUrl ? getCachedExerciseVideoThumbnail(this.props.videoUrl) : undefined,
    };

    componentDidMount() {
        this.loadFullVideoPreview();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.videoUrl !== this.props.videoUrl) {
            this.revokeCachedVideoUrl();
            this.setState(
                {
                    cachedVideoUrl: undefined,
                    hasLoadError: false,
                    isLoading: false,
                    thumbnailUrl: this.props.videoUrl ? getCachedExerciseVideoThumbnail(this.props.videoUrl) : undefined,
                },
                this.loadFullVideoPreview,
            );
        }
    }

    componentWillUnmount() {
        this.aborted = true;
        this.revokeCachedVideoUrl();
    };

    onVideoError = () => {
        this.setState({ hasLoadError: true });
    };

    loadFullVideoPreview = async () => {
        const { videoUrl } = this.props;

        if (!videoUrl) {
            return;
        }

        this.setState({ isLoading: true });

        try {
            const videoBlob = await getCachedExerciseVideoBlob(videoUrl);

            if (this.aborted) {
                return;
            }

            const cachedVideoUrl = URL.createObjectURL(videoBlob);
            const thumbnailUrl = this.state.thumbnailUrl || await this.ensureFormThumbnail(videoUrl, cachedVideoUrl);

            if (!this.aborted) {
                this.setState({ cachedVideoUrl, hasLoadError: false, isLoading: false, thumbnailUrl });
            } else {
                URL.revokeObjectURL(cachedVideoUrl);
            }
        } catch {
            if (!this.aborted) {
                this.setState({ hasLoadError: true, isLoading: false });
            }
        }
    };

    ensureFormThumbnail = async (videoUrl: string, cachedVideoUrl: string): Promise<string | undefined> => {
        const cachedThumbnailUrl = getCachedExerciseVideoThumbnail(videoUrl);

        if (cachedThumbnailUrl) {
            return cachedThumbnailUrl;
        }

        try {
            const thumbnailDataUrl = await createExerciseVideoThumbnail(cachedVideoUrl);

            setCachedExerciseVideoThumbnail(videoUrl, thumbnailDataUrl);
            return thumbnailDataUrl;
        } catch {
            return undefined;
        }
    };

    revokeCachedVideoUrl = () => {
        if (this.state.cachedVideoUrl) {
            URL.revokeObjectURL(this.state.cachedVideoUrl);
        }
    };

    render() {
        const { title, videoUrl } = this.props;
        const { cachedVideoUrl, hasLoadError, isLoading, thumbnailUrl } = this.state;

        if (!videoUrl || hasLoadError) {
            return (
                <Paper
                    sx={{
                        alignItems: 'center',
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        minHeight: 220,
                        p: 2,
                    }}
                    variant="outlined"
                >
                    <Typography color="text.secondary" sx={{ textAlign: 'center' }} variant="body2">
                        {hasLoadError ? 'Video preview unavailable for this URL.' : 'Add a video URL to preview this exercise.'}
                    </Typography>
                </Paper>
            );
        }

        return (
            <Box>
                <Box
                    component="video"
                    controls
                    muted
                    onError={this.onVideoError}
                    poster={thumbnailUrl}
                    playsInline
                    preload="metadata"
                    ref={this.playbackVideoRef}
                    src={cachedVideoUrl || videoUrl}
                    sx={{
                        backgroundColor: 'common.black',
                        borderRadius: 2,
                        display: 'block',
                        height: 260,
                        objectFit: 'cover',
                        width: '100%',
                    }}
                />
                <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                    <Typography color="text.secondary" variant="body2">
                        {isLoading ? 'Caching video preview...' : `Previewing ${title}`}
                    </Typography>
                    <Button href={videoUrl} rel="noopener noreferrer" size="small" startIcon={<OpenInNewRoundedIcon />} target="_blank">
                        Open Video
                    </Button>
                </Box>
            </Box>
        );
    }
}

export default ExerciseVideoPreview;